import {useMemo} from "react";
import {AccountFeature, Role, SystemRole} from "platformed-client/types";
import {q, useQueryData} from "../state";
import {useAccountFeatures} from "./accountFeatures";
import {useIsAdmin} from "./roles";

// The per-app system roles that gate product access. Each value is both an
// AccountFeature and the SystemRole that grants it (the names coincide). Mirrors
// `SystemRole::app_feature` on the backend.
export const PRODUCT_ROLES: (AccountFeature & SystemRole)[] = ["VendorToolkit", "TPRM"];

/** The set of product features the current user can access. */
export function useAccessibleProducts(): Set<AccountFeature> {
  const features = useAccountFeatures();
  const isAdmin = useIsAdmin();
  const whoami = useQueryData(q.whoAmIOnAccount());

  return useMemo(() => {
    const accessible = new Set<AccountFeature>();
    for (const role of PRODUCT_ROLES) {
      if (features.has(role) && (isAdmin || whoami.roles.some(r => r.system_role === role))) {
        accessible.add(role);
      }
    }
    return accessible;
  }, [features, isAdmin, whoami.roles]);
}

/**
 * Whether the current user can access the given product/feature. Per-app
 * products require the account to have the feature enabled AND the user to be an
 * admin or hold the per-app role (directly or via a team — resolved server-side).
 * Features that aren't gated products are accessible whenever they're enabled.
 */
export function useCanAccessProduct(feature: AccountFeature): boolean {
  const features = useAccountFeatures();
  const accessibleProducts = useAccessibleProducts();

  if (!features.has(feature)) return false;
  // Non-product features are accessible whenever enabled.
  if (!(PRODUCT_ROLES as AccountFeature[]).includes(feature)) return true;
  return accessibleProducts.has(feature);
}

/**
 * The account's roles, with per-app roles hidden when their application isn't
 * enabled. The backend returns the full list; this is the cosmetic filtering the
 * role pickers want.
 */
export function useVisibleRoles(): Role[] {
  const roles = useQueryData(q.roles());
  const features = useAccountFeatures();

  return useMemo(
    () =>
      roles.filter(role => {
        const systemRole = role.system_role;
        if (systemRole && (PRODUCT_ROLES as SystemRole[]).includes(systemRole)) {
          return features.has(systemRole as AccountFeature);
        }
        return true;
      }),
    [roles, features],
  );
}
